import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Clear,
  PlayArrow,
  Refresh,
  ExpandMore,
  ExpandLess,
  Email,
  Psychology,
  VerticalAlignBottom,
  VerticalAlignTop
} from '@mui/icons-material';
import LogService, { LogEntry } from '../services/LogService';

const LiveLogsPanel: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [connected, setConnected] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [testingGmail, setTestingGmail] = useState(false);
  const [testingOllama, setTestingOllama] = useState(false);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [autoScroll, setAutoScroll] = useState(true);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);
  const lastScrollTime = useRef(0);
  const isAutoScrolling = useRef(false);

  useEffect(() => {
    loadInitialLogs();
    connectWebSocket();

    return () => {
      LogService.disconnect();
    };
  }, []);

  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      isAutoScrolling.current = true;
      setTimeout(() => {
        if (logsEndRef.current) {
          logsEndRef.current.scrollIntoView({ behavior: 'auto' });
        }
        // Mark auto-scrolling as complete after a brief delay
        setTimeout(() => {
          isAutoScrolling.current = false;
        }, 50);
      }, 10);
    }
  }, [logs, autoScroll]);

  const loadInitialLogs = async () => {
    try {
      const response = await LogService.getLogs(50);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to load initial logs:', error);
    }
  };

  const connectWebSocket = () => {
    const socket = LogService.connect();

    socket.on('connect', () => {
      setConnected(true);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    socket.on('log', (logEntry: LogEntry) => {
      setLogs(prev => [...prev, logEntry].slice(-200));
    });

    socket.on('logs-history', (logEntries: LogEntry[]) => {
      setLogs(logEntries);
    });

    socket.on('logs-cleared', () => {
      setLogs([]);
    });
  };

  const handleClearLogs = async () => {
    setClearing(true);
    try {
      await LogService.clearLogs();
    } catch (error) {
      console.error('Failed to clear logs:', error);
    } finally {
      setClearing(false);
    }
  };

  const handleTriggerProcessing = async () => {
    setProcessing(true);
    try {
      await LogService.triggerEmailProcessing();
    } catch (error) {
      console.error('Failed to trigger email processing:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleTestGmail = async () => {
    setTestingGmail(true);
    try {
      await LogService.testGmailConnection();
    } catch (error) {
      console.error('Failed to test Gmail connection:', error);
    } finally {
      setTestingGmail(false);
    }
  };

  const handleTestOllama = async () => {
    setTestingOllama(true);
    try {
      await LogService.testAIConnection();
    } catch (error) {
      console.error('Failed to test AI connection:', error);
    } finally {
      setTestingOllama(false);
    }
  };

  const toggleLogExpansion = (logId: number) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'error';
      case 'warn': return 'warning';
      case 'success': return 'success';
      case 'info': return 'info';
      case 'debug': return 'default';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const handleScroll = () => {
    // Do not automatically disable autoscroll when user scrolls
    // User must manually control autoscroll via toggle button
    if (!listRef.current || isAutoScrolling.current) return;
    
    const now = Date.now();
    lastScrollTime.current = now;
  };

  const handleToggleAutoScroll = () => {
    const newAutoScroll = !autoScroll;
    setAutoScroll(newAutoScroll);
    
    if (newAutoScroll && logsEndRef.current) {
      isAutoScrolling.current = true;
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        isAutoScrolling.current = false;
      }, 300);
    }
  };

  const scrollToBottom = () => {
    if (logsEndRef.current) {
      isAutoScrolling.current = true;
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setAutoScroll(true);
      setTimeout(() => {
        isAutoScrolling.current = false;
      }, 300);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Live Logs
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip 
              label={connected ? 'Connected' : 'Disconnected'} 
              color={connected ? 'success' : 'error'}
              size="small"
            />
            <Tooltip title={autoScroll ? 'Disable Auto-scroll' : 'Enable Auto-scroll'}>
              <Button
                variant={autoScroll ? "contained" : "outlined"}
                size="small"
                onClick={handleToggleAutoScroll}
                startIcon={autoScroll ? <VerticalAlignBottom /> : <VerticalAlignTop />}
                color={autoScroll ? 'primary' : 'inherit'}
              >
                Auto-scroll
              </Button>
            </Tooltip>
            {!autoScroll && (
              <Tooltip title="Scroll to bottom">
                <IconButton size="small" onClick={scrollToBottom}>
                  <VerticalAlignBottom />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Test Ollama AI">
              <Button
                variant="outlined"
                size="small"
                onClick={handleTestOllama}
                disabled={testingOllama}
                startIcon={testingOllama ? <CircularProgress size={16} /> : <Psychology />}
                color="info"
              >
                {testingOllama ? 'Testing...' : 'Test AI'}
              </Button>
            </Tooltip>
            <Tooltip title="Test Gmail Connection">
              <Button
                variant="outlined"
                size="small"
                onClick={handleTestGmail}
                disabled={testingGmail}
                startIcon={testingGmail ? <CircularProgress size={16} /> : <Email />}
                color="secondary"
              >
                {testingGmail ? 'Testing...' : 'Test Gmail'}
              </Button>
            </Tooltip>
            <Tooltip title="Test Email Processing">
              <Button
                variant="outlined"
                size="small"
                onClick={handleTriggerProcessing}
                disabled={processing}
                startIcon={processing ? <CircularProgress size={16} /> : <PlayArrow />}
              >
                {processing ? 'Processing...' : 'Test Now'}
              </Button>
            </Tooltip>
            <Tooltip title="Refresh Logs">
              <IconButton size="small" onClick={loadInitialLogs}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear Logs">
              <IconButton 
                size="small" 
                onClick={handleClearLogs}
                disabled={clearing}
              >
                {clearing ? <CircularProgress size={16} /> : <Clear />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {logs.length === 0 ? (
          <Alert severity="info">
            No logs available. Trigger email processing to see activity.
          </Alert>
        ) : (
          <Box
            ref={listRef}
            sx={{
              maxHeight: 400,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'grey.50'
            }}
          >
            <List dense>
              {logs.map((log) => (
                <ListItem
                  key={log.id}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                        <Chip 
                          label={log.level.toUpperCase()} 
                          color={getLevelColor(log.level) as any}
                          size="small"
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {log.message}
                        </Typography>
                        {log.data && (
                          <IconButton
                            size="small"
                            onClick={() => toggleLogExpansion(log.id)}
                          >
                            {expandedLogs.has(log.id) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        )}
                      </Box>
                    }
                    secondary={
                      log.data && expandedLogs.has(log.id) && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {log.data}
                          </Typography>
                        </Box>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
            <div ref={logsEndRef} />
          </Box>
        )}
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Showing last {logs.length} logs • Auto-scroll: {autoScroll ? 'ON' : 'OFF'} • v1.1.5
        </Typography>
      </CardContent>
    </Card>
  );
};

export default LiveLogsPanel;